import React, { useState, useEffect, useRef } from 'react';
import { Link, useParams, useLocation, useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dropdown } from 'primereact/dropdown';
import { Dialog } from 'primereact/dialog';
import { Calendar } from 'primereact/calendar';
import { InputSwitch } from 'primereact/inputswitch';
import { Toast } from 'primereact/toast';
import { makeRequest2 } from '../../../../../utils/makeRequest';
import { smart_meters_base_url, customer_account_base_url, customerBillingTriggerURL } from "../../../../../utils/urls";
import { toastify } from '../../../../../utils/toast';
import Layout from '../../../component/layout';
import { useFacilityStore } from '../../../../../app/z-store/store';

function ViewCustomer() {
    const navigate = useNavigate();
    const { meterId } = useParams();
    const location = useLocation();
    const accountId = location.state?.accountId;
    const toast = useRef(null);
    const selectedFacilityId = useFacilityStore((state) => state.facilityId);
    
    const defaultCustomerData = {
        name: 'N/A',
        id: 'N/A',
        customerId: 'N/A',
        accountNumber: 'N/A',
        meterNumber: 'N/A',
        status: 'N/A',
        phone: 'N/A',
        email: 'N/A'
    };

    const [customerData, setCustomerData] = useState(defaultCustomerData);
    const [loading, setLoading] = useState(false);
    const [invoices, setInvoices] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [lazyParams, setLazyParams] = useState({
        first: 0,
        rows: 10,
        page: 0,
    });
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState(null);
    const [yearMonthFilter, setYearMonthFilter] = useState(null);
    const dt = useRef(null);

    const [generateVisible, setGenerateVisible] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [generateData, setGenerateData] = useState({
        mode: 'month',
        yearMonth: null,
        dateRange: null,
        sendNotifications: false
    });

    const statusOptions = [
        { label: 'All Statuses', value: null },
        { label: 'Paid', value: 'Paid' },
        { label: 'Partially Paid', value: 'Partially Paid' },
        { label: 'Cancelled', value: 'Cancelled' },
        { label: 'Overdue', value: 'Overdue' },
        { label: 'Unpaid', value: 'Unpaid' }
    ];

    const generateYearMonthOptions = () => {
        const options = [{ label: 'All Months', value: null }];
        const now = new Date();
        for (let i = 0; i < 12; i++) {
            const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
            const yearMonth = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
            const label = date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
            options.push({ label, value: yearMonth });
        }
        return options;
    };

    const yearMonthOptions = generateYearMonthOptions();

    const openGenerateDialog = () => {
        setGenerateData({ mode: 'month', yearMonth: null, dateRange: null, sendNotifications: false });
        setGenerateVisible(true);
    };

    const toDateString = (d) =>
        `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

    const handleGenerateBill = async () => {
        const payload = { sendNotifications: generateData.sendNotifications };

        if (generateData.mode === 'month') {
            if (!generateData.yearMonth) {
                toastify('Please select a month', 'error');
                return;
            }
            payload.yearMonth = generateData.yearMonth;
        } else {
            const [start, end] = generateData.dateRange || [];
            if (!start || !end) {
                toastify('Please select a start and end date', 'error');
                return;
            }
            payload.startDate = toDateString(start);
            payload.endDate = toDateString(end);
        }

        try {
            setGenerating(true);
            const response = await makeRequest2(
                customerBillingTriggerURL(selectedFacilityId, customerData.customerId),
                'POST',
                payload,
                { timeout: 5 * 60 * 1000 }
            );

            if (!response.success) {
                throw new Error(response.message || response.error || 'Failed to generate bill');
            }

            const { analog, smart } = response.data?.data || response.data || {};
            const created = (analog?.invoicesCreated || 0) + (smart?.invoicesCreated || 0);
            if (created > 0) {
                toastify(`Bill generated (${created} invoice${created > 1 ? 's' : ''})`, 'success');
                setGenerateVisible(false);
                fetchCustomerInvoices();
            } else {
                const reason = analog?.error || smart?.error || analog?.message || smart?.message
                    || 'No invoice was created. An invoice may already exist, or there is no reviewed reading / meter history for this period.';
                toastify(reason, 'warn');
            }
        } catch (error) {
            toastify(error.message || 'Failed to generate bill', 'error');
        } finally {
            setGenerating(false);
        }
    };

    useEffect(() => {
        if (accountId) {
            fetchCustomerDataById(accountId);
        } else if (meterId) {
            fetchCustomerDataByMeterId(meterId);
        }
    }, [accountId, meterId]);

    useEffect(() => {
        if (customerData.accountNumber !== 'N/A' && selectedFacilityId) {
            fetchCustomerInvoices();
        }
    }, [customerData.accountNumber, lazyParams, statusFilter, yearMonthFilter, searchTerm, selectedFacilityId]);

    const fetchCustomerDataById = async (id) => {
        try {
            setLoading(true);
            const response = await makeRequest2(
                `${customer_account_base_url}/get/${id}`,
                'GET',
                null
            );

            if (response.success) {
                const accountData = response.data.data;
                setCustomerData({
                    id: accountData._id || accountData.id,
                    customerId: accountData.customerId || accountData._id,
                    name: accountData.customerName || 'N/A',
                    accountNumber: accountData.account_no || 'N/A',
                    meterNumber: accountData.meterNumber || 'N/A',
                    status: accountData.status || 'N/A',
                    phone: accountData.phoneNumber || 'N/A',
                    email: accountData.email || 'N/A'
                });
            } else {
                toastify('Error fetching customer data', 'error');
            }
        } catch (error) {
            toastify('Error fetching customer data', 'error');
        } finally {
            setLoading(false);
        }
    };

    const fetchCustomerDataByMeterId = async (meterId) => {
        try {
            setLoading(true);
            const response = await makeRequest2(
                `${smart_meters_base_url}/customer/${meterId}`,
                'GET',
                null
            );

            if (response.success) {
                const accountData = response.data;
                setCustomerData({
                    id: accountData._id || accountData.id,
                    customerId: accountData.customerId || accountData._id,
                    name: accountData.customerName || 'N/A',
                    accountNumber: accountData.account_no || 'N/A',
                    meterNumber: accountData.meterNumber || 'N/A',
                    status: accountData.status || 'N/A',
                    phone: accountData.phoneNumber || 'N/A',
                    email: accountData.email || 'N/A'
                });
            } else {
                toastify('Error fetching customer data', 'error');
            }
        } catch (error) {
            toastify('Error fetching customer data', 'error');
        } finally {
            setLoading(false);
        }
    };

    const fetchCustomerInvoices = async () => {
        try {
            setLoading(true);
            const skip = lazyParams.first;
            const limit = lazyParams.rows;

            // Include both accountNumber AND customerId for accurate filtering
            let endpoint = `/api/app/water-invoices/get/${customerData.accountNumber}?facilityId=${selectedFacilityId}&customerId=${customerData.customerId}&billingType=postpaid&limit=${limit}&skip=${skip}`;

            if (statusFilter) {
                endpoint += `&status=${statusFilter}`;
            }
            if (yearMonthFilter) {
                endpoint += `&yearMonth=${yearMonthFilter}`;
            }

            const response = await makeRequest2(endpoint, 'GET');
            if (response.success) {
                const invoicesData = response.data.data?.invoices || [];
                setInvoices(invoicesData);
                setTotalRecords(response.data?.totalCount || 0);
            } else {
                toastify('Failed to fetch customer invoices', 'error');
            }
        } catch (error) {
            console.error('Error fetching invoices:', error);
            toastify('Error loading customer invoices', 'error');
        } finally {
            setLoading(false);
        }
    };

    const onPage = (event) => {
        setLazyParams(event);
    };

    const handleSearchChange = (value) => {
        setSearchTerm(value);
        setLazyParams(prev => ({
            ...prev,
            first: 0,
            page: 0
        }));
    };

    const handleStatusChange = (value) => {
        setStatusFilter(value);
        setLazyParams(prev => ({
            ...prev,
            first: 0,
            page: 0
        }));
    };

    const handleYearMonthChange = (value) => {
        setYearMonthFilter(value);
        setLazyParams(prev => ({
            ...prev,
            first: 0,
            page: 0
        }));
    };

    const handleView = (invoice) => {
        navigate("/facility/utility_management/invoice_management/invoice_page", {
            state: {
                invoiceId: invoice._id,
                invoiceNumber: invoice.invoiceNumber
            }
        });
    };

    const statusBodyTemplate = (rowData) => {
        const statusColors = {
            'Paid': 'bg-success text-light',
            'Partially Paid': 'bg-warning text-light',
            'Cancelled': 'bg-secondary text-light',
            'Overdue': 'bg-danger text-light',
            'Unpaid': 'bg-danger text-light'
        };
        return (
            <span className={`badge ${statusColors[rowData.status] || 'bg-secondary text-light'}`}>
                {rowData.status || 'unknown'}
            </span>
        );
    };

    const formatNumber = (value) => {
        return new Intl.NumberFormat('en-KE', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(value);
    };

    const invoiceAmountBodyTemplate = (rowData) => {
        return formatNumber(rowData.charges?.totalMonthlyBill || 0);
    };

    const amountPaidBodyTemplate = (rowData) => {
        return formatNumber(rowData.amountPaid || 0);
    };

    const balanceBodyTemplate = (rowData) => {
        const balance = (rowData.charges?.totalMonthlyBill || 0) - (rowData.amountPaid || 0);
        return formatNumber(balance);
    };

    const dateBodyTemplate = (rowData, field) => {
        return rowData[field] ? new Date(rowData[field]).toLocaleDateString() : 'N/A';
    };

    const exportCSV = async () => {
        try {
            let endpoint = `/api/app/water-invoices/get/${customerData.accountNumber}?facilityId=${selectedFacilityId}&billingType=postpaid&limit=10000&skip=0`;

            if (statusFilter) {
                endpoint += `&status=${statusFilter}`;
            }
            if (yearMonthFilter) {
                endpoint += `&yearMonth=${yearMonthFilter}`;
            }
            if (searchTerm) {
                endpoint += `&searchTerm=${encodeURIComponent(searchTerm)}`;
            }

            const response = await makeRequest2(endpoint, 'GET');
            if (response.success) {
                const allInvoices = response.data?.invoices || [];

                const header = [
                    'Invoice No', 'Invoice Date', 'Invoice (KES)', 'Paid Amount (KES)',
                    'Balance (KES)', 'Status', 'Due Date', 'Month'
                ];

                const data = allInvoices.map(invoice => [
                    invoice.invoiceNumber,
                    new Date(invoice.dateIssued).toLocaleDateString(),
                    invoice.charges?.totalMonthlyBill || 0,
                    invoice.amountPaid || 0,
                    (invoice.charges?.totalMonthlyBill || 0) - (invoice.amountPaid || 0),
                    invoice.status,
                    new Date(invoice.dueDate).toLocaleDateString(),
                    invoice.yearMonth
                ]);

                const csvContent = [
                    header.join(','),
                    ...data.map(row => row.map(cell =>
                        typeof cell === 'string' && cell.includes(',')
                            ? `"${cell}"`
                            : cell
                    ).join(','))
                ].join('\n');

                const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.setAttribute('href', url);
                link.setAttribute(
                    'download',
                    `Customer_Invoices_${customerData.accountNumber}_${new Date()
                        .toISOString()
                        .slice(0, 10)}.csv`
                );
                link.style.visibility = 'hidden';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                toast.current.show({ 
                    severity: 'success', 
                    summary: 'Success', 
                    detail: 'Invoices exported to CSV', 
                    life: 3000 
                });
            }
        } catch (error) {
            console.error('Error exporting CSV:', error);
            toast.current.show({ 
                severity: 'error', 
                summary: 'Error', 
                detail: 'Failed to export invoices', 
                life: 3000 
            });
        }
    };

    const clearFilters = () => {
        setSearchTerm('');
        setStatusFilter(null);
        setYearMonthFilter(null);
        setLazyParams({
            first: 0,
            rows: 10,
            page: 0,
        });
    };

    return (
        <Layout>
            <div className="page-header">
                <div className="page-block">
                    <div className="row align-items-center">
                        <div className="col-md-12">
                            <ul className="breadcrumb">
                                <li className="breadcrumb-item">
                                    <Link to="/facility/">Dashboard</Link>
                                </li>
                                <li className="breadcrumb-item">
                                    <Link to="/facility/utility_management/meter_management/water_meters">
                                        Water Meters
                                    </Link>
                                </li>
                                <li className="breadcrumb-item active">
                                    <span>View Customer</span>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>

            <div className="row">
                <div className="col-sm-12">
                    <div className="card">
                        <div className="card-header">
                            <Link
                                to="/facility/utility_management/meter_management/water_meters"
                                state={{ activeTab: "customers" }}
                            >
                                <i className="ti ti-arrow-narrow-left"></i> Back
                            </Link>
                        </div>
                    </div>

                    {loading && customerData.id === 'N/A' ? (
                        <div className="card">
                            <div className="card-body">
                                <div className="d-flex justify-content-center">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <>
                            {/* Customer Info Card */}
                            <div className="card mb-3">
                                <div className="card-body">
                                    <div className="row">
                                        <div className="col-md-12 d-flex align-items-center mb-3">
                                            <div className="avatar avatar-xl bg-light-primary rounded-circle me-3">
                                                <i className="ti ti-user f-28"></i>
                                            </div>
                                            <div>
                                                <h4 className="mb-1">{customerData.name}</h4>
                                                <span className={`badge ${customerData.status.toLowerCase() === 'active' ? 'bg-success' : 'bg-danger'}`}>
                                                    {customerData.status}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="col-md-6 col-lg-3 mb-2">
                                            <small className="text-muted">Account Number</small>
                                            <p className="mb-0 fw-bold">{customerData.accountNumber}</p>
                                        </div>
                                        <div className="col-md-6 col-lg-3 mb-2">
                                            <small className="text-muted">Meter Number</small>
                                            <p className="mb-0 fw-bold">{customerData.meterNumber}</p>
                                        </div>
                                        <div className="col-md-6 col-lg-3 mb-2">
                                            <small className="text-muted">Phone Number</small>
                                            <p className="mb-0 fw-bold">{customerData.phone}</p>
                                        </div>
                                        <div className="col-md-6 col-lg-3 mb-2">
                                            <small className="text-muted">Email</small>
                                            <p className="mb-0 fw-bold">{customerData.email}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Invoices Table */}
                            <div className="card">
                                <div className="card-body">
                                    <Toast ref={toast} />
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <h5 className="card-title mb-0">Postpaid Invoices</h5>
                                        <Button
                                            label="Generate Bill"
                                            icon="ti ti-file-invoice"
                                            onClick={openGenerateDialog}
                                            disabled={customerData.customerId === 'N/A'}
                                        />
                                    </div>
                                    
                                    {/* Filters */}
                                    <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
                                        <div className="d-flex col-md-8 col-xs-12 gap-2 mb-2 mb-md-0">
                                            <div className="col-md-4">
                                                <input
                                                    className="form-control"
                                                    placeholder="Search invoices..."
                                                    value={searchTerm}
                                                    onChange={(e) => handleSearchChange(e.target.value)}
                                                />
                                            </div>
                                            <div className="col-md-3">
                                                <Dropdown
                                                    value={statusFilter}
                                                    options={statusOptions}
                                                    onChange={(e) => handleStatusChange(e.value)}
                                                    placeholder="Filter by Status"
                                                    className="w-100"
                                                />
                                            </div>
                                            <div className="col-md-3">
                                                <Dropdown
                                                    value={yearMonthFilter}
                                                    options={yearMonthOptions}
                                                    onChange={(e) => handleYearMonthChange(e.value)}
                                                    placeholder="Filter by Month"
                                                    className="w-100"
                                                />
                                            </div>
                                            <div className="col-md-2">
                                                <Button
                                                    label="Clear"
                                                    icon="ti ti-x"
                                                    className="p-button-outlined w-100"
                                                    onClick={clearFilters}
                                                />
                                            </div>
                                        </div>
                                        <div className="d-flex gap-2">
                                            <Button
                                                label="CSV"
                                                icon="ti ti-file-spreadsheet"
                                                className="p-button-success"
                                                onClick={exportCSV}
                                            />
                                        </div>
                                    </div>

                                    <div className="dt-responsive table-responsive">
                                        <DataTable
                                            ref={dt}
                                            value={invoices}
                                            emptyMessage="No invoices found for this customer."
                                            sortMode="multiple"
                                            paginator
                                            lazy
                                            first={lazyParams.first}
                                            rows={lazyParams.rows}
                                            totalRecords={totalRecords}
                                            onPage={onPage}
                                            loading={loading}
                                            stripedRows
                                            tableStyle={{ minWidth: '50rem' }}
                                            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                                            currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                                            rowsPerPageOptions={[10, 20, 50, 100]}
                                        >
                                            <Column field="invoiceNumber" header="Inv No" sortable />
                                            <Column
                                                field="dateIssued"
                                                header="Invoice Date"
                                                body={(rowData) => dateBodyTemplate(rowData, 'dateIssued')}
                                                sortable
                                            />
                                            <Column
                                                header="Invoice (KES)"
                                                body={invoiceAmountBodyTemplate}
                                            />
                                            <Column
                                                field="amountPaid"
                                                header="Paid (KES)"
                                                body={amountPaidBodyTemplate}
                                            />
                                            <Column
                                                header="Balance (KES)"
                                                body={balanceBodyTemplate}
                                            />
                                            <Column field="status" header="Status" body={statusBodyTemplate} sortable />
                                            <Column
                                                field="dueDate"
                                                header="Due Date"
                                                body={(rowData) => dateBodyTemplate(rowData, 'dueDate')}
                                            />
                                            <Column field="yearMonth" header="Month" sortable />
                                            <Column
                                                header="Actions"
                                                body={(rowData) => (
                                                    <Button
                                                        icon="ti ti-eye"
                                                        className="p-button-rounded p-button-text"
                                                        onClick={() => handleView(rowData)}
                                                        tooltip="View Invoice"
                                                    />
                                                )}
                                            />
                                        </DataTable>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
                        <Dialog
                visible={generateVisible}
                onHide={() => !generating && setGenerateVisible(false)}
                header={`Generate Bill - ${customerData.name}`}
                style={{ width: '500px' }}
                modal
                footer={
                    <div>
                        <Button label="Cancel" className="p-button-text" onClick={() => setGenerateVisible(false)} disabled={generating} />
                        <Button label="Generate" icon="ti ti-check" onClick={handleGenerateBill} loading={generating} />
                    </div>
                }
            >
                <p className="text-muted">
                    Generates a postpaid water invoice for this customer only (account {customerData.accountNumber}).
                </p>

                <div className="mb-3">
                    <label className="fw-bold mb-2 d-block">Bill by</label>
                    <Dropdown
                        className="w-100"
                        value={generateData.mode}
                        options={[
                            { label: 'Month', value: 'month' },
                            { label: 'Custom period', value: 'period' }
                        ]}
                        onChange={(e) => setGenerateData((prev) => ({ ...prev, mode: e.value }))}
                    />
                </div>

                {generateData.mode === 'month' ? (
                    <div className="mb-3">
                        <label className="fw-bold mb-2 d-block">Month <span style={{ color: 'red' }}>*</span></label>
                        <Dropdown
                            className="w-100"
                            value={generateData.yearMonth}
                            options={yearMonthOptions.filter((opt) => opt.value !== null)}
                            onChange={(e) => setGenerateData((prev) => ({ ...prev, yearMonth: e.value }))}
                            placeholder="Select month"
                        />
                    </div>
                ) : (
                    <div className="mb-3">
                        <label className="fw-bold mb-2 d-block">Period <span style={{ color: 'red' }}>*</span></label>
                        <Calendar
                            className="w-100"
                            selectionMode="range"
                            value={generateData.dateRange}
                            onChange={(e) => setGenerateData((prev) => ({ ...prev, dateRange: e.value }))}
                            placeholder="Select start and end date"
                            dateFormat="yy-mm-dd"
                            readOnlyInput
                            appendTo="self"
                        />
                    </div>
                )}

                <div className="d-flex align-items-center gap-2">
                    <InputSwitch
                        checked={generateData.sendNotifications}
                        onChange={(e) => setGenerateData((prev) => ({ ...prev, sendNotifications: e.value }))}
                    />
                    <label className="mb-0">Send notification to customer after generating</label>
                </div>
            </Dialog>
        </Layout>
    );
}

export default ViewCustomer;